import React, { useMemo, useState } from "react";
import { useLaboBall } from "./LaboBall";
import { useMediaQuery } from "../../helpers/useMediaQuery";
import {
  computeNumberRateStats,
  effectiveDrawSize,
  KENO_MAX_NUMBER,
  type LaboDraw,
  type RateStatus,
} from "./laboStats";
import styles from "./labo.module.css";

interface Props {
  tiragesDesc: LaboDraw[];
  /** Les tirages sont l'exemple figé de la page publique (premiers tirages de la formule). */
  exemple?: boolean;
}

type Filter = "tous" | RateStatus;

/*
 * Libellés de position face au taux théorique, sans « en retard » ni « en avance » : ces mots
 * laissent entendre qu'un numéro va « rattraper » son écart, alors que chaque tirage est indépendant.
 */
const FILTERS: { key: Filter; label: string }[] = [
  { key: "tous", label: "Tous" },
  { key: "retard", label: "Sous le taux" },
  { key: "neutre", label: "Dans la norme" },
  { key: "avance", label: "Au-dessus" },
];

const STATUS_CLASS: Record<RateStatus, string> = {
  retard: styles.badgeRetard,
  neutre: styles.badgeNeutre,
  avance: styles.badgeAvance,
};

const STATUS_LABEL: Record<RateStatus, string> = {
  retard: "Sous le taux",
  neutre: "Dans la norme",
  avance: "Au-dessus du taux",
};

/* Libellés courts des écrans étroits : le statut tient sur une ligne, le libellé long reste lu. */
const STATUS_SHORT: Record<RateStatus, string> = {
  retard: "Sous",
  neutre: "Norme",
  avance: "Au-dessus",
};

const pct = (r: number) => `${(r * 100).toFixed(1).replace(".", ",")} %`;

/**
 * Outil « Taux de sortie contre taux théorique » (← classeur « ultra KENO complet »).
 * Compare la fréquence de sortie réelle de chaque numéro au taux théorique
 * d'un tirage (16/56 ≈ 28,6 %) et le place sous le taux, dans la norme ou au-dessus.
 */
export const TauxTheoriqueTool: React.FC<Props> = ({ tiragesDesc, exemple = false }) => {
  const Ball = useLaboBall();
  const [filter, setFilter] = useState<Filter>("tous");
  // Écran étroit : le taux observé (sorties / tirages) laisse la place à l'écart et au statut.
  const narrow = useMediaQuery("(max-width: 480px)");

  const stats = useMemo(() => computeNumberRateStats(tiragesDesc), [tiragesDesc]);
  const theoretical = useMemo(
    () => effectiveDrawSize(tiragesDesc) / KENO_MAX_NUMBER,
    [tiragesDesc]
  );

  const counts = useMemo(
    () => ({
      retard: stats.filter((s) => s.status === "retard").length,
      avance: stats.filter((s) => s.status === "avance").length,
    }),
    [stats]
  );

  const rows = useMemo(() => {
    const filtered =
      filter === "tous" ? stats : stats.filter((s) => s.status === filter);
    return [...filtered].sort((a, b) => a.deltaPct - b.deltaPct);
  }, [stats, filter]);

  return (
    <div className={styles.card}>
      <div className={styles.verdict}>
        <p className={styles.verdictSentence}>
          {exemple
            ? `Sur les ${tiragesDesc.length} tirages de l'exemple figé,`
            : `Sur les ${tiragesDesc.length} derniers tirages,`}{" "}
          <span className={styles.verdictAccent}>{counts.retard}</span> numéros sortent sous le taux
          théorique et <span className={styles.verdictAccent}>{counts.avance}</span> au-dessus.
        </p>
        <div className={styles.verdictPills}>
          <span className={`${styles.pill} ${styles.pillNeutral}`}>
            taux théorique {pct(theoretical)}
          </span>
        </div>
      </div>

      <div className={styles.controlsRow}>
        <span className={styles.controlLabel}>Filtrer</span>
        <div className={`${styles.segmented} ${styles.segmentedQuatre}`} role="group" aria-label="Filtrer">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.segmentedBtn} ${filter === f.key ? styles.segmentedBtnActive : ""}`}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`${styles.tableWrap} ${styles.tableWrapTaux}`}>
        <table className={`${styles.table} ${styles.tableTaux}`}>
          <thead>
            <tr>
              <th scope="col" className={styles.numCol}>N°</th>
              <th scope="col" className={styles.tdRight}>Sorties</th>
              {!narrow && <th scope="col" className={styles.tdRight}>Taux observé</th>}
              <th scope="col" className={styles.tdRight}>
                <span className={styles.thLong}>Écart au taux théorique</span>
                <span className={styles.thShort} aria-hidden="true">Écart</span>
              </th>
              <th scope="col" className={styles.tdCenter}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => (
              <tr
                key={s.number}
                className={i > 0 && rows[i - 1].status !== s.status ? styles.rowGroupStart : undefined}
              >
                <td>
                  <Ball number={s.number} />
                </td>
                <td className={styles.tdRight}>{s.appearances}</td>
                {!narrow && <td className={styles.tdRight}>{pct(s.observedRate)}</td>}
                <td className={styles.tdRight}>
                  {s.deltaPct >= 0 ? "+" : ""}
                  {s.deltaPct.toFixed(0)} %
                </td>
                <td className={styles.tdCenter}>
                  <span className={`${styles.badge} ${STATUS_CLASS[s.status]}`}>
                    <span className={styles.thLong}>{STATUS_LABEL[s.status]}</span>
                    <span className={styles.thShort} aria-hidden="true">{STATUS_SHORT[s.status]}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.methodNote}>
        Le <strong>taux théorique</strong> d'un numéro est sa probabilité d'apparaître dans un tirage
        donné : {effectiveDrawSize(tiragesDesc)} numéros tirés sur {KENO_MAX_NUMBER}, soit{" "}
        {pct(theoretical)}. L'écart affiché compare la fréquence réellement observée à ce taux.
        Chaque tirage reste indépendant : ces écarts sont des observations historiques, pas une
        indication sur les tirages à venir.
      </p>
    </div>
  );
};
